import { createElement } from "react";
import { toast } from "react-toastify";

const CLIENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || "";
const UPDATE_TOAST_ID = "smbf-app-update";
const COLD_RELOAD_FLAG = "smbf-cold-sw-reload";
const APPLYING_UPDATE_FLAG = "smbf-applying-update";
const SNOOZE_FLAG = "smbf-update-snoozed-build";

let pendingRegistration: ServiceWorkerRegistration | null = null;
let updatePromptShown = false;
let userHasInteracted = false;
let applyingUpdate = false;

function trackUserInteraction() {
  const mark = () => {
    userHasInteracted = true;
  };
  ["pointerdown", "keydown", "touchstart", "input", "change"].forEach((type) => {
    window.addEventListener(type, mark, { once: true, capture: true });
  });
}

function askWaitingWorkerToActivate(registration: ServiceWorkerRegistration) {
  registration.waiting?.postMessage({ type: "SKIP_WAITING" });
}

async function clearAppCaches() {
  if (!("caches" in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    /* ignore */
  }
}

/** Hard reload so we pick up new index.html + hashed bundles, not a soft SPA refresh. */
function hardReload() {
  const url = new URL(window.location.href);
  url.searchParams.set("_sw", Date.now().toString());
  window.location.replace(url.toString());
}

async function applyUpdateAndReload() {
  applyingUpdate = true;
  sessionStorage.setItem(APPLYING_UPDATE_FLAG, "1");
  toast.dismiss(UPDATE_TOAST_ID);

  try {
    if (pendingRegistration?.waiting) {
      askWaitingWorkerToActivate(pendingRegistration);
    }
    await clearAppCaches();
  } catch {
    /* still reload */
  }

  hardReload();
}

/** Fresh tab / cold start only — never while the user is mid-form. */
function reloadOnceIfColdStart(): boolean {
  if (userHasInteracted) return false;
  if (sessionStorage.getItem(COLD_RELOAD_FLAG) === "1") return false;
  if (sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") return false;
  sessionStorage.setItem(COLD_RELOAD_FLAG, "1");
  hardReload();
  return true;
}

function isSnoozedForCurrentDeploy(buildId?: string) {
  const target = buildId || "";
  if (!target) return false;
  return sessionStorage.getItem(SNOOZE_FLAG) === target;
}

function snoozeForDeploy(buildId?: string) {
  if (buildId) {
    sessionStorage.setItem(SNOOZE_FLAG, buildId);
  }
}

function showUpdateAvailablePrompt(deployBuildId?: string) {
  if (applyingUpdate || sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") {
    return;
  }
  if (isSnoozedForCurrentDeploy(deployBuildId)) {
    return;
  }
  if (updatePromptShown) return;
  updatePromptShown = true;

  const buttonStyle = {
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
  } as const;

  toast.info(
    ({ closeToast }) =>
      createElement(
        "div",
        { style: { display: "flex", flexDirection: "column", gap: 8 } },
        createElement("span", null, "A new version of SMBF Connect is available."),
        createElement(
          "div",
          { style: { display: "flex", gap: 8 } },
          createElement(
            "button",
            {
              type: "button",
              onClick: () => {
                closeToast?.();
                void applyUpdateAndReload();
              },
              style: {
                ...buttonStyle,
                border: "none",
                background: "#031b3d",
                color: "#fff",
              },
            },
            "Refresh",
          ),
          createElement(
            "button",
            {
              type: "button",
              onClick: () => {
                snoozeForDeploy(deployBuildId || "unknown");
                updatePromptShown = false;
                closeToast?.();
              },
              style: {
                ...buttonStyle,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "transparent",
                fontWeight: 500,
              },
            },
            "Later",
          ),
        ),
      ),
    {
      toastId: UPDATE_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    },
  );
}

function onUpdateReady(registration: ServiceWorkerRegistration) {
  pendingRegistration = registration;

  if (applyingUpdate || sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") {
    askWaitingWorkerToActivate(registration);
    return;
  }

  if (!userHasInteracted) {
    askWaitingWorkerToActivate(registration);
    reloadOnceIfColdStart();
    return;
  }

  showUpdateAvailablePrompt();
}

function checkForUpdates(registration: ServiceWorkerRegistration) {
  registration.update().catch(() => {
    /* ignore offline / aborted update checks */
  });

  if (registration.waiting && navigator.serviceWorker.controller) {
    onUpdateReady(registration);
  }
}

async function checkDeployedVersion() {
  if (!CLIENT_BUILD_ID || process.env.NODE_ENV !== "production") return;
  if (applyingUpdate || sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") return;

  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return;
    const data = (await res.json()) as { buildId?: string };
    if (!data.buildId || data.buildId === "dev" || data.buildId === CLIENT_BUILD_ID) {
      sessionStorage.removeItem(COLD_RELOAD_FLAG);
      sessionStorage.removeItem(APPLYING_UPDATE_FLAG);
      sessionStorage.removeItem(SNOOZE_FLAG);
      updatePromptShown = false;
      return;
    }

    if (isSnoozedForCurrentDeploy(data.buildId)) {
      return;
    }

    if (!userHasInteracted) {
      reloadOnceIfColdStart();
      return;
    }

    showUpdateAvailablePrompt(data.buildId);
  } catch {
    /* offline */
  }
}

function scheduleVersionChecks() {
  void checkDeployedVersion();
  setInterval(() => void checkDeployedVersion(), 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void checkDeployedVersion();
    }
  });
  window.addEventListener("focus", () => void checkDeployedVersion());
}

/** Register PWA service worker (production only). */
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  if (!("serviceWorker" in navigator)) return;

  pendingRegistration = null;
  updatePromptShown = false;
  userHasInteracted = false;
  applyingUpdate = sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1";

  trackUserInteraction();
  scheduleVersionChecks();

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;

    // User clicked Refresh (or we are mid-apply) — reload once, never re-open the toast.
    if (applyingUpdate || sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") {
      refreshing = true;
      hardReload();
      return;
    }

    if (reloadOnceIfColdStart()) {
      refreshing = true;
      return;
    }

    // Do not show the toast from controllerchange alone — version/SW checks own that.
  });

  window.addEventListener("load", () => {
    // After a forced update reload, drop the flag once the page has loaded.
    if (sessionStorage.getItem(APPLYING_UPDATE_FLAG) === "1") {
      // Keep flag until version matches CLIENT_BUILD_ID (cleared in checkDeployedVersion).
      applyingUpdate = true;
      void clearAppCaches();
      void checkDeployedVersion();
    }

    const publicUrl = process.env.PUBLIC_URL || "";
    const swUrl = publicUrl ? `${publicUrl}/sw.js` : "/sw.js";

    navigator.serviceWorker
      .register(swUrl, { updateViaCache: "none" })
      .then((registration) => {
        pendingRegistration = registration;

        if (registration.waiting && navigator.serviceWorker.controller) {
          onUpdateReady(registration);
        }

        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              onUpdateReady(registration);
            }
          });
        });

        checkForUpdates(registration);
        setInterval(() => checkForUpdates(registration), 5 * 60 * 1000);

        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            checkForUpdates(registration);
          }
        });
        window.addEventListener("focus", () => checkForUpdates(registration));
      })
      .catch((error) => {
        console.error("[Service Worker] Registration failed:", error);
      });
  });
}

export function unregisterServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister();
    });
  });
}
